import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import CommodityLimit from './commodityLimit';
import merchantRiskService from './merchantRiskService';
import { AgGridColumn, AgGridReact } from '@ag-grid-community/react';
import defaultGridOptions from 'common/agGrid/defaultGridOptions';
import NumberEditor from 'common/ui/NumberEditor';
import RenderParameters from 'common/agGrid/renderParameters';
import ObjectState from 'common/objectState';
import AsyncButton from 'common/ui/AsyncButton';
import AddCommodityModal from './AddCommodityModal';
import Commodity from 'domain/commodity';
import merchantService from '../merchantService';
import TypedSelect from 'common/ui/TypedSelect';

export interface CommodityLimitSettingsProps {
    merchantId: string;
    disabled: boolean;
}


const defaultCommodityLimitValues = {
    dayPurchaseLimit: 10_000,
    daySaleLimit: 10_000,
    nightPurchaseLimit: 10_000,
    nightSaleLimit: 10_000,

    autoPositionDayPurchaseLimit: 10_000,
    autoPositionDaySaleLimit: 10_000,
    autoPositionNightPurchaseLimit: 10_000,
    autoPositionNightSaleLimit: 10_000,
    autoPositionNightMaxFrequencyMilliseconds: 2000
}

const maxFrequencyOptions = [
    { text: ".25", value: 250 },
    { text: ".5", value: 500 },
    { text: "1", value: 1000 },
    { text: "2", value: 2000 },
    { text: "3", value: 3000 }
];

const CommodityLimitSettings: React.FC<CommodityLimitSettingsProps> = props => {

    const [commodityLimits, setCommodityLimits] = useState<(CommodityLimit & ObjectState)[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [commodities, setCommodities] = useState<Commodity[]>(null);
    const [availableCommodities, setAvailableCommodities] = useState<Commodity[]>(null);

    useEffect(() => {
        if (props.merchantId == null) return;

        loadLimits();
        loadCommodities();
    }, [props.merchantId]);

    useEffect(() => {
        if (commodityLimits == null) return;
        setHasChanges(commodityLimits.some(m => m.isNew || m.isModified));
    }, [commodityLimits]);

    useEffect(() => {
        if (commodities == null || commodityLimits == null) return;

        setAvailableCommodities(_.differenceBy(commodities, commodityLimits.map(m => m.commodity), m => m.id))
    }, [commodities, commodityLimits]);

    async function loadLimits() {
        try {
            const limits = await merchantRiskService.listCommodityLimits(props.merchantId);

            setCommodityLimits(_.orderBy(limits, m => m.commodity.name).map(m => ObjectState.asClean(m)));
        }
        catch (err) {
            loggingService.error("Unable to retrieve risk limits from server", err as Error);
            notificationService.error("Unable to retrieve risk limits")
        }
    }

    async function loadCommodities() {
        try {
            const commodities = await merchantService.listCommodities(props.merchantId);

            setCommodities(commodities);
        }
        catch (err) {
            loggingService.error("Unable to retrieve commodities from server", err as Error);
            notificationService.error("Unable to retrieve commodities")
        }
    }


    function addCommodity(commodity: Commodity) {
        setCommodityLimits([...commodityLimits, {...ObjectState.asNew(defaultCommodityLimitValues), commodity: {...commodity, name: commodity.displayName}}]);  
        setShowAddModal(false);
    }

    async function saveChanges() {
        try {
            await Promise.all(commodityLimits.map(m => saveLimit(m)))
            await loadLimits();
        }
        catch (err) {
            loggingService.error("Unable to save changes", err as Error);
            notificationService.error("Unable to save changes");
        }
    }

    async function saveLimit(limit: CommodityLimit & ObjectState) {
        if (limit.isNew || limit.isModified) {
            await merchantRiskService.updateLimit(props.merchantId, limit);
        }
    }

    async function reset() {
        await loadLimits();
    }

    function updateLimit(limit: CommodityLimit) {
        setCommodityLimits(commodityLimits.map(m => m.commodity.id === limit.commodity.id 
            ? { ...limit, isNew: m.isNew, isModified: true }
            : m))
    }

    return <div className="ag-theme-alpine">
        <div className="row table-header-row ml-0 mr-0">
                <div className="col">
                    <div className="page-sub-header">
                    Commodity Limits
                    </div>
                </div>
                <div className="col action-bar">
                    <button onClick={() => setShowAddModal(true)} className="btn btn-link ml-auto"
                        disabled={props.disabled}>
                        <i className="material-icons">add_circle_outline</i> Add Commodity
                    </button>
                </div>
            </div>
            <AgGridReact
                {...defaultGridOptions}
                paginationPageSize={25}
                pagination
                rowData={commodityLimits}
                getRowId={params => params.data.commodity.id}
                domLayout="autoHeight"

                onGridReady={params => params.api.sizeColumnsToFit()}>
                <AgGridColumn field="commodity.name" headerName="Commodity"
                    width={150} />
                    <AgGridColumn headerName="Day" headerClass="group-header">
                <AgGridColumn headerName="All Orders" headerClass="group-header" >
                    <AgGridColumn field="dayPurchaseLimit" headerName="Purchase"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, dayPurchaseLimit: val})} />
                        }
                        width={100}
                    />
                    <AgGridColumn field="daySaleLimit" headerName="Sale"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, daySaleLimit: val})} />
                        }
                        width={100}
                    />
                </AgGridColumn>
                <AgGridColumn headerName="Auto Position" headerClass="group-header" >
                    <AgGridColumn field="autoPositionDayPurchaseLimit" headerName="Purchase"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, autoPositionDayPurchaseLimit: val})} />
                        }
                        width={100}
                    />
                    <AgGridColumn field="autoPositionDaySaleLimit" headerName="Sale"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, autoPositionDaySaleLimit: val})} />
                        }
                        width={100}
                    />
                </AgGridColumn>
                </AgGridColumn>
                <AgGridColumn headerName="Night" headerClass="group-header" >
                <AgGridColumn headerName="All Orders" headerClass="group-header" >
                    <AgGridColumn field="nightPurchaseLimit" headerName="Purchase"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, nightPurchaseLimit: val})} />
                        }
                        width={100}
                    />
                    <AgGridColumn field="nightSaleLimit" headerName="Sale"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, nightSaleLimit: val})} />
                        }
                        width={100}
                    />
                </AgGridColumn>
                <AgGridColumn headerName="Auto Position" headerClass="group-header" >
                    <AgGridColumn field="autoPositionNightPurchaseLimit" headerName="Purchase"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, autoPositionNightPurchaseLimit: val})} />
                        }
                        width={100}
                    />
                    <AgGridColumn field="autoPositionNightSaleLimit" headerName="Sale"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <LimitEditor value={params.value} 
                                disabled={props.disabled}
                                onChange={val => updateLimit({...params.node.data, autoPositionNightSaleLimit: val})} />
                        }
                        width={100}
                    />
                    <AgGridColumn field="autoPositionNightMaxFrequencyMilliseconds" headerName="Max Frequency (s)"
                        cellRenderer={(params: RenderParameters<CommodityLimit>) => 
                            <TypedSelect className="ml-auto"
                                options={maxFrequencyOptions}
                                value={{value: params.value}}
                                textGetter={m => m.text}
                                valueGetter={m => m.value}
                                onValueSelected={m => updateLimit({...params.node.data, autoPositionNightMaxFrequencyMilliseconds: m.value})}
                                disabled={props.disabled}
                                />
                        }
                        width={120}
                    />
                </AgGridColumn>
                </AgGridColumn>
            </AgGridReact>

            <div className="mt-2">
                <AsyncButton className="pull-right"
                    disabled={!hasChanges}
                    onClickAsync={saveChanges}>
                    Save
                </AsyncButton>
                <AsyncButton className="pull-right mr-2"
                    variant='secondary'
                    disabled={!hasChanges}
                    onClickAsync={reset}>
                    Reset
                </AsyncButton>
            </div>

            {showAddModal && <AddCommodityModal 
                commodities={availableCommodities}
                onAdd={addCommodity}
                onHide={() => setShowAddModal(false)}/>}
    </div>;
}

interface LimitEditorProps<T> {
    value: T;
    onChange(value: T): void;
    disabled: boolean;
}

const LimitEditor: React.FC<LimitEditorProps<number>> = props => <NumberEditor 
    value={props.value}
    min={0}
    thousandSeparator
    onValueChange={props.onChange}
    disabled={props.disabled} />

export default CommodityLimitSettings;